import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { WizardPreview } from "@/components/app/WizardPreview";
import { ProductFormPreview } from "@/components/app/ProductPreview";
import { defaultWizardConfig } from "@/lib/defaultWizardConfig";

import { ConfigLinker } from "@/components/app/ConfigLinker";
import { WizardAssistant } from "@/components/app/WizardAssistant";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Play, AlertCircle, BookOpen, Save, Trash2, Package, Wand2, ArrowLeft, Camera, Undo2, Image as ImageIcon, Upload, X, Loader2, Link2, PanelLeftClose, PanelLeftOpen, ChevronDown, Check } from "lucide-react";
import { defaultPhotographerConfig } from "@/lib/defaultPhotographerConfig";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as Sentry from "@sentry/react";

import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

interface SavedConfig {
  id: string;
  name: string;
  config: any;
  type: string;
  region_key: string | null;
  updated_at: string;
}

type EditorTab = "brief" | "product" | "photographer";

import { REGION_MAP, REGION_OPTIONS } from "@/lib/regions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Extract region_key from parsed JSON config (top-level field) */
function extractRegionKey(config: any): string | null {
  if (!config || typeof config !== "object") return null;
  return config.region_key || null;
}

export default function WizardBuilder() {
  const { user } = useAuth();
  const { configId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const typeParam = searchParams.get("type") as EditorTab | null;
  const regionParam = searchParams.get("region");

  // Editor state
  const [editorTab, setEditorTab] = useState<EditorTab>(typeParam === "product" ? "product" : typeParam === "photographer" ? "photographer" : "brief");
  const [jsonText, setJsonText] = useState("");
  const [productJsonText, setProductJsonText] = useState("");
  const [parsedConfig, setParsedConfig] = useState<any>(null);
  const [parsedProduct, setParsedProduct] = useState<any>(null);
  const [parsedPhotographer, setParsedPhotographer] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Archive state — separate for each tab
  const [briefConfigs, setBriefConfigs] = useState<SavedConfig[]>([]);
  const [productConfigs, setProductConfigs] = useState<SavedConfig[]>([]);
  const [photographerConfigs, setPhotographerConfigs] = useState<SavedConfig[]>([]);
  const [activeBriefId, setActiveBriefId] = useState<string | null>(null);
  const [activeBriefName, setActiveBriefName] = useState("Untitled Brief");
  const [activeProductId, setActiveProductId] = useState<string | null>(null);
  const [activeProductName, setActiveProductName] = useState("Untitled Product");
  const [activePhotographerId, setActivePhotographerId] = useState<string | null>(null);
  const [activePhotographerName, setActivePhotographerName] = useState("Untitled Photographer");
  const [photographerJsonText, setPhotographerJsonText] = useState("");
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Shown inside the delete dialog. The page-level `error` renders behind it,
  // so a failure reported there is invisible while the dialog is open.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteUsage, setDeleteUsage] = useState<{ briefs: number; visuals: number } | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [jsonCollapsed, setJsonCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("zitiu.backoffice.wizardBuilder.jsonCollapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("zitiu.backoffice.wizardBuilder.jsonCollapsed", jsonCollapsed ? "1" : "0"); } catch {}
  }, [jsonCollapsed]);

  // Workspace assignments
  const [allWorkspaces, setAllWorkspaces] = useState<{ id: string; name: string }[]>([]);
  const [assignedWorkspaceIds, setAssignedWorkspaceIds] = useState<Set<string>>(new Set());
  const [togglingWorkspaceId, setTogglingWorkspaceId] = useState<string | null>(null);

  // Undo history per tab — pushed every time the assistant applies a change
  const [briefHistory, setBriefHistory] = useState<string[]>([]);
  const [productHistory, setProductHistory] = useState<string[]>([]);
  const [photographerHistory, setPhotographerHistory] = useState<string[]>([]);

  // Illustration upload state (product tab only)
  const [illustrationUploading, setIllustrationUploading] = useState(false);
  const [illustrationError, setIllustrationError] = useState<string | null>(null);

  // Read the illustration URL out of the current product JSON, if valid
  const currentIllustration = (() => {
    if (editorTab !== "product" || !productJsonText.trim()) return null;
    try {
      const parsed = JSON.parse(productJsonText);
      return typeof parsed?.illustration === "string" ? parsed.illustration : null;
    } catch {
      return null;
    }
  })();

  // Derived active state based on tab
  const activeId = editorTab === "brief" ? activeBriefId : editorTab === "photographer" ? activePhotographerId : activeProductId;
  const activeName = editorTab === "brief" ? activeBriefName : editorTab === "photographer" ? activePhotographerName : activeProductName;
  const setActiveName = editorTab === "brief" ? setActiveBriefName : editorTab === "photographer" ? setActivePhotographerName : setActiveProductName;
  const activeJsonText = editorTab === "brief" ? jsonText : editorTab === "photographer" ? photographerJsonText : productJsonText;
  const setActiveJsonText = editorTab === "brief" ? setJsonText : editorTab === "photographer" ? setPhotographerJsonText : setProductJsonText;
  const savedList = editorTab === "brief" ? briefConfigs : editorTab === "photographer" ? photographerConfigs : productConfigs;
  const activeHistory = editorTab === "brief" ? briefHistory : editorTab === "photographer" ? photographerHistory : productHistory;
  const setActiveHistory = editorTab === "brief" ? setBriefHistory : editorTab === "photographer" ? setPhotographerHistory : setProductHistory;

  // Apply a JSON change from the assistant — pushes current value onto undo history,
  // updates the editor, and persists to the database immediately so the user can test it.
  const applyAssistantJson = async (newJson: string) => {
    setActiveHistory((prev) => [...prev, activeJsonText].slice(-20));
    setActiveJsonText(newJson);
    setError(null);
    await persistJson(newJson);
  };

  const undoLastApply = async () => {
    if (activeHistory.length === 0) return;
    const previous = activeHistory[activeHistory.length - 1];
    setActiveHistory((prev) => prev.slice(0, -1));
    setActiveJsonText(previous);
    setError(null);
    await persistJson(previous);
  };

  // Update the top-level `illustration` field in the current product JSON,
  // then save the config so the studio picks it up immediately.
  const setIllustrationInProductJson = async (url: string | null) => {
    let parsed: any;
    try {
      parsed = productJsonText.trim() ? JSON.parse(productJsonText) : {};
    } catch (e: any) {
      setIllustrationError(`Cannot update — invalid JSON: ${e.message}`);
      return;
    }
    if (url) parsed.illustration = url;
    else delete parsed.illustration;
    const next = JSON.stringify(parsed, null, 2);
    setProductJsonText(next);
    await persistJson(next);
  };

  const handleIllustrationUpload = async (file: File) => {
    if (!user) return;
    if (!file.type.startsWith("image/")) {
      setIllustrationError("Please choose an image file (PNG, JPG, SVG…).");
      return;
    }
    setIllustrationError(null);
    setIllustrationUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2, 9)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("config-illustrations")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("config-illustrations").getPublicUrl(path);
      await setIllustrationInProductJson(urlData.publicUrl);
    } catch (e: any) {
      console.error("Illustration upload failed:", e);
      Sentry.captureException(e);
      setIllustrationError(e?.message || "Upload failed");
    } finally {
      setIllustrationUploading(false);
    }
  };

  const handleClearIllustration = async () => {
    setIllustrationError(null);
    await setIllustrationInProductJson(null);
  };

  /**
   * The two shortcuts write a root-relative path to a bundled asset, so the
   * file has to exist in BOTH apps: the value is resolved against whichever
   * origin renders it — the backoffice here, Studio in the product picker.
   * apps/backoffice/public/ did not exist at all, so this preview was a broken
   * image while the same value rendered fine in Studio. Keep the copies under
   * each app's public/illustrations directory in sync.
   */
  const handleUseDefaultIllustration = async (url: string) => {
    setIllustrationError(null);
    await setIllustrationInProductJson(url);
  };

  // Load all configs
  const loadConfigs = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("wizard_configs")
      .select("id, name, config, type, region_key, updated_at")
      .order("updated_at", { ascending: false, nullsFirst: false });
    if (error) {
      console.error("Failed to load wizard configs:", error);
      Sentry.captureException(error);
    }
    if (data) {
      setBriefConfigs((data as SavedConfig[]).filter((d) => d.type === "brief"));
      setProductConfigs((data as SavedConfig[]).filter((d) => d.type === "product"));
      setPhotographerConfigs((data as SavedConfig[]).filter((d) => d.type === "photographer"));
    }
  }, [user]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // Load workspace assignments when editing an existing config
  const loadWorkspaceAssignments = useCallback(async (cfgId: string) => {
    const [wsResult, assignResult] = await Promise.all([
      supabase.from("workspaces").select("id, name").order("name"),
      supabase.from("workspace_wizard_configs").select("workspace_id").eq("wizard_config_id", cfgId),
    ]);
    if (wsResult.data) setAllWorkspaces(wsResult.data);
    if (assignResult.data) setAssignedWorkspaceIds(new Set(assignResult.data.map((r: any) => r.workspace_id)));
  }, []);

  const handleToggleWorkspace = async (workspaceId: string) => {
    const cfgId = activeId;
    if (!cfgId || !user) return;
    setTogglingWorkspaceId(workspaceId);

    const isAssigned = assignedWorkspaceIds.has(workspaceId);
    if (isAssigned) {
      const { error } = await supabase
        .from("workspace_wizard_configs")
        .delete()
        .eq("workspace_id", workspaceId)
        .eq("wizard_config_id", cfgId);
      if (error) {
        console.error("Failed to unassign workspace:", error);
        Sentry.captureException(error);
      } else {
        setAssignedWorkspaceIds((prev) => { const next = new Set(prev); next.delete(workspaceId); return next; });
      }
    } else {
      const { error } = await supabase
        .from("workspace_wizard_configs")
        .insert({ workspace_id: workspaceId, wizard_config_id: cfgId, assigned_by: user.id });
      if (error) {
        console.error("Failed to assign workspace:", error);
        Sentry.captureException(error);
      } else {
        setAssignedWorkspaceIds((prev) => new Set(prev).add(workspaceId));
      }
    }
    setTogglingWorkspaceId(null);
  };

  // Load config by route param
  useEffect(() => {
    if (!configId || configId === "new" || !user) return;
    const loadById = async () => {
      const { data, error } = await supabase
        .from("wizard_configs")
        .select("id, name, config, type, region_key, updated_at")
        .eq("id", configId)
        .single();
      if (error || !data) {
        if (error) {
          console.error("Failed to load wizard config:", error);
          Sentry.captureException(error);
        }
        navigate("/wizard-builder", { replace: true });
        return;
      }
      if (data) {
        const t = (data as SavedConfig).type as EditorTab;
        setEditorTab(t);
        if (t === "brief") {
          setActiveBriefId(data.id);
          setActiveBriefName(data.name);
          setJsonText(JSON.stringify(data.config, null, 2));
        } else if (t === "photographer") {
          setActivePhotographerId(data.id);
          setActivePhotographerName(data.name);
          setPhotographerJsonText(JSON.stringify(data.config, null, 2));
        } else {
          setActiveProductId(data.id);
          setActiveProductName(data.name);
          setProductJsonText(JSON.stringify(data.config, null, 2));
        }
      }
      loadWorkspaceAssignments(data.id);
    };
    loadById();
  }, [configId, user]);

  // When creating a new brief with a region param, pre-fill with default config for that region
  useEffect(() => {
    if (configId === "new" && typeParam === "brief" && regionParam) {
      const regionInfo = REGION_MAP[regionParam];
      if (regionInfo) {
        const config = {
          ...defaultWizardConfig,
          region_key: regionParam,
          region_label: regionInfo.label,
          regionPromptFragment: defaultWizardConfig.regionPromptFragment, // user should customize
        };
        setJsonText(JSON.stringify(config, null, 2));
        setActiveBriefName(`${regionInfo.label} Brief`);
      }
    } else if (configId === "new" && typeParam === "brief" && !regionParam) {
      // Global / region-free brief
      const config = {
        ...defaultWizardConfig,
        region_key: null,
        region_label: null,
        regionPromptFragment: "",
      };
      setJsonText(JSON.stringify(config, null, 2));
      setActiveBriefName("Global Brief");
    } else if (configId === "new" && typeParam === "photographer") {
      setPhotographerJsonText(JSON.stringify(defaultPhotographerConfig, null, 2));
      setActivePhotographerName("New Photographer Style");
    }
  }, [configId, typeParam, regionParam]);


  const handlePreview = () => {
    let briefParsed: any = null;
    let productParsed: any = null;
    let photographerParsed: any = null;

    if (jsonText.trim()) {
      try {
        briefParsed = JSON.parse(jsonText);
        if (!briefParsed.steps || !Array.isArray(briefParsed.steps)) {
          setError("Brief config must have a 'steps' array");
          return;
        }
      } catch (e: any) {
        setError(`Invalid Brief JSON: ${e.message}`);
        return;
      }
    }

    if (productJsonText.trim()) {
      try {
        productParsed = JSON.parse(productJsonText);
      } catch (e: any) {
        setError(`Invalid Product JSON: ${e.message}`);
        return;
      }
    }

    if (photographerJsonText.trim()) {
      try {
        photographerParsed = JSON.parse(photographerJsonText);
      } catch (e: any) {
        setError(`Invalid Photographer JSON: ${e.message}`);
        return;
      }
    }

    if (!briefParsed && !productParsed && !photographerParsed) {
      setError("Enter at least one JSON config to preview");
      return;
    }

    setParsedConfig(briefParsed);
    setParsedProduct(productParsed);
    setParsedPhotographer(photographerParsed);
    setError(null);
  };

  const handleReset = () => {
    setParsedConfig(null);
    setParsedProduct(null);
    setParsedPhotographer(null);
    setError(null);
  };

  const handleNew = () => {
    if (editorTab === "brief") {
      setActiveBriefId(null);
      setActiveBriefName("Untitled Brief");
      setJsonText("");
    } else if (editorTab === "photographer") {
      setActivePhotographerId(null);
      setActivePhotographerName("Untitled Photographer");
      setPhotographerJsonText("");
    } else {
      setActiveProductId(null);
      setActiveProductName("Untitled Product");
      setProductJsonText("");
    }
    setParsedConfig(null);
    setParsedProduct(null);
    setParsedPhotographer(null);
    setError(null);
  };

  const persistJson = async (jsonText: string): Promise<boolean> => {
    if (!user) return false;
    if (!jsonText.trim()) return false;

    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e: any) {
      setError(`Invalid JSON: ${e.message}`);
      return false;
    }

    // Extract region_key from the JSON for briefs
    const regionKey = editorTab === "brief" ? extractRegionKey(parsed) : null;

    setLoading(true);
    if (activeId) {
      const { error: updateErr } = await supabase
        .from("wizard_configs")
        .update({
          config: parsed,
          name: activeName,
          region_key: regionKey,
          updated_at: new Date().toISOString(),
        })
        .eq("id", activeId);
      if (updateErr) {
        console.error("Failed to update wizard config:", updateErr);
        Sentry.captureException(updateErr);
        setError("Failed to save changes.");
        setLoading(false);
        return false;
      }
    } else {
      const { data, error: insertErr } = await supabase
        .from("wizard_configs")
        .insert({
          name: activeName,
          config: parsed,
          type: editorTab,
          region_key: regionKey,
          user_id: user.id,
          updated_at: new Date().toISOString(),
        })
        .select("id")
        .single();
      if (insertErr) {
        console.error("Failed to create wizard config:", insertErr);
        Sentry.captureException(insertErr);
        setError("Failed to create config.");
        setLoading(false);
        return false;
      }
      if (data) {
        if (editorTab === "brief") setActiveBriefId(data.id);
        else if (editorTab === "photographer") setActivePhotographerId(data.id);
        else setActiveProductId(data.id);
        navigate(`/wizard-builder/${data.id}`, { replace: true });
        loadWorkspaceAssignments(data.id);
      }
    }
    await loadConfigs();
    setLoading(false);
    return true;
  };

  const handleSave = () => persistJson(activeJsonText);

  const handleLoad = (item: SavedConfig) => {
    if (item.type === "brief") {
      setActiveBriefId(item.id);
      setActiveBriefName(item.name);
      setJsonText(JSON.stringify(item.config, null, 2));
    } else if (item.type === "photographer") {
      setActivePhotographerId(item.id);
      setActivePhotographerName(item.name);
      setPhotographerJsonText(JSON.stringify(item.config, null, 2));
    } else {
      setActiveProductId(item.id);
      setActiveProductName(item.name);
      setProductJsonText(JSON.stringify(item.config, null, 2));
    }
    setParsedConfig(null);
    setParsedProduct(null);
    setError(null);
  };

  const handleRename = async () => {
    if (!activeId || !renameValue.trim()) return;
    setLoading(true);
    const { error } = await supabase
      .from("wizard_configs")
      .update({ name: renameValue.trim(), updated_at: new Date().toISOString() })
      .eq("id", activeId);
    if (error) {
      console.error("Failed to rename wizard config:", error);
      Sentry.captureException(error);
    } else {
      setActiveName(renameValue.trim());
    }
    setRenameOpen(false);
    await loadConfigs();
    setLoading(false);
  };

  /**
   * Count what points at this config before offering to delete it.
   *
   * Both references are ON DELETE SET NULL, so the delete succeeds either way
   * — the point is that deleting a config used by 200 visuals should not look
   * identical to deleting an unused one.
   */
  const openDeleteDialog = async () => {
    setDeleteError(null);
    setDeleteUsage(null);
    setDeleteOpen(true);
    if (!activeId) return;
    const [briefRes, visualRes] = await Promise.all([
      supabase.from("briefs").select("id", { count: "exact", head: true }).eq("wizard_config_id", activeId),
      supabase.from("visuals").select("id", { count: "exact", head: true }).eq("brief_preset_id", activeId),
    ]);
    setDeleteUsage({ briefs: briefRes.count || 0, visuals: visualRes.count || 0 });
  };

  const handleDelete = async () => {
    if (!activeId) return;
    setLoading(true);
    setDeleteError(null);
    const { error } = await supabase.from("wizard_configs").delete().eq("id", activeId);
    if (error) {
      // This used to return after a console line, leaving the dialog open and
      // the button apparently dead. Every rejection now says what happened.
      console.error("Failed to delete wizard config:", error);
      Sentry.captureException(error);
      setDeleteError(
        error.code === "23503"
          ? `Something still references this config, so the database refused the delete: ${error.message}`
          : error.message || "The delete failed for an unknown reason.",
      );
      setLoading(false);
      return;
    }
    setDeleteOpen(false);
    setLoading(false);
    navigate("/wizard-builder");
  };

  /**
   * Set (or clear) the brief's region by writing `region_key` into the JSON.
   *
   * The DB column is derived from this field on save, so the JSON is the source
   * of truth. Previously region could only be set via a ?region= URL param when
   * first creating a brief, which meant an existing brief could never be moved
   * between regions from the UI — it silently stayed global.
   */
  const setRegion = (regionKey: string | null) => {
    try {
      const parsed = JSON.parse(jsonText);
      if (regionKey) {
        parsed.region_key = regionKey;
        parsed.region_label = REGION_MAP[regionKey]?.label ?? null;
      } else {
        parsed.region_key = null;
        parsed.region_label = null;
      }
      setJsonText(JSON.stringify(parsed, null, 2));
    } catch {
      // Invalid JSON in the editor — leave it alone rather than clobbering work.
    }
  };

  // Get current region from JSON for display
  const currentRegionKey = (() => {
    if (editorTab !== "brief" || !jsonText.trim()) return null;
    try {
      return extractRegionKey(JSON.parse(jsonText));
    } catch {
      return null;
    }
  })();
  const currentRegion = currentRegionKey ? REGION_MAP[currentRegionKey] : null;

  const tabLabel = editorTab === "brief" ? "Brief" : editorTab === "photographer" ? "Photographer" : "Product";


  return (
    <div className="h-screen flex flex-col p-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 shrink-0 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate("/wizard-builder")} className="shrink-0 h-8 w-8">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="min-w-0 flex-1 flex items-center gap-1.5">
          <button
            onClick={() => activeId && (setRenameValue(activeName), setRenameOpen(true))}
            className="text-base font-semibold truncate text-left hover:underline decoration-dotted underline-offset-4"
            title={activeId ? "Click to rename" : undefined}
          >
            {activeName}
          </button>
          {editorTab === "brief" && (
            <Select
              value={currentRegionKey ?? "__global__"}
              onValueChange={(v) => setRegion(v === "__global__" ? null : v)}
            >
              <SelectTrigger className="h-6 w-auto gap-1 px-1.5 text-[10px] shrink-0 border-border bg-secondary">
                <SelectValue>
                  <span className="flex items-center gap-1">
                    <span>{currentRegion?.emoji || "\u{1F310}"}</span>
                    <span className="text-muted-foreground">{currentRegion?.label || "Global"}</span>
                  </span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__global__">🌐 Global (no region)</SelectItem>
                {REGION_OPTIONS.map((r) => (
                  <SelectItem key={r.key} value={r.key}>
                    {r.emoji} {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!activeId && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 shrink-0">unsaved</Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
          {editorTab === "brief" && activeId && (
            <Button size="sm" variant="outline" className="h-8" onClick={() => setConfigDialogOpen(true)}>
              <Link2 className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Config</span>
              <span className="ml-1 text-muted-foreground">({assignedWorkspaceIds.size})</span>
            </Button>
          )}

          {activeHistory.length > 0 && (
            <Button size="sm" variant="outline" className="h-8" onClick={undoLastApply}>
              <Undo2 className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Undo ({activeHistory.length})</span>
              <span className="sm:hidden ml-0.5">{activeHistory.length}</span>
            </Button>
          )}

          <Button size="sm" variant="default" className="h-8" onClick={handleSave} disabled={loading}>
            <Save className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Save</span>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setJsonCollapsed((c) => !c)}
            title={jsonCollapsed ? "Show JSON editor" : "Hide JSON editor"}
          >
            {jsonCollapsed ? <PanelLeftOpen className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
          </Button>

          <Button variant="ghost" size="icon" className="h-8 w-8" asChild title="JSON Reference">
            <Link to="/wizard-reference">
              <BookOpen className="w-3.5 h-3.5" />
            </Link>
          </Button>

          {activeId && (
            <Button size="icon" variant="ghost" onClick={() => void openDeleteDialog()} className="text-destructive hover:text-destructive h-8 w-8" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Main editor */}
      <div className="flex-1 min-h-0 rounded-xl border border-border overflow-hidden">
        <ResizablePanelGroup direction="horizontal" key={jsonCollapsed ? "collapsed" : "expanded"}>
          {!jsonCollapsed && (
          <>
          <ResizablePanel defaultSize={45} minSize={30}>
            <div className="flex flex-col h-full bg-card">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
                {!configId && !typeParam && (
                <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
                  <button
                    onClick={() => setEditorTab("brief")}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                      editorTab === "brief"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Wand2 className="w-3 h-3" />
                    Brief JSON
                  </button>
                  <button
                    onClick={() => setEditorTab("product")}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                      editorTab === "product"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Package className="w-3 h-3" />
                    Product JSON
                  </button>
                  <button
                    onClick={() => setEditorTab("photographer")}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                      editorTab === "photographer"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Camera className="w-3 h-3" />
                    Photographer
                  </button>
                </div>
                )}
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={!jsonText.trim() && !productJsonText.trim() && !photographerJsonText.trim()}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
                >
                  <Play className="w-3 h-3" />
                  Preview
                </button>
              </div>

              {error && (
                <div className="mx-4 mt-3 p-3 rounded-lg border border-destructive/50 bg-destructive/5 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-xs text-destructive">{error}</p>
                </div>
              )}

              <div className="flex-1 p-4 min-h-0 flex flex-col gap-4 overflow-auto">
                {editorTab === "product" && (
                  <div className="rounded-lg border border-border bg-card p-2.5">
                    <p className="text-xs font-medium mb-0.5">Illustration</p>
                    <p className="text-[11px] text-muted-foreground mb-2">
                      A picture for this form. Shown in Studio when a customer picks
                      what kind of product they are adding — nothing else uses it.
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-md bg-muted/50 border border-border flex items-center justify-center overflow-hidden shrink-0">
                        {currentIllustration ? (
                          <img src={currentIllustration} alt="" className="w-full h-full object-contain p-0.5" />
                        ) : (
                          <ImageIcon className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-background text-xs font-medium cursor-pointer hover:bg-muted">
                            {illustrationUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {illustrationUploading ? "Uploading…" : "Upload"}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={illustrationUploading}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleIllustrationUpload(f);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => handleUseDefaultIllustration("/illustrations/hvac-outdoor.svg")}
                            className="px-2.5 py-1 rounded-md border border-border bg-background text-xs font-medium hover:bg-muted"
                            title="Use the stock drawing of an outdoor unit"
                          >
                            Outdoor unit drawing
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUseDefaultIllustration("/illustrations/hvac-indoor.svg")}
                            className="px-2.5 py-1 rounded-md border border-border bg-background text-xs font-medium hover:bg-muted"
                            title="Use the stock drawing of an indoor unit"
                          >
                            Indoor unit drawing
                          </button>
                          {currentIllustration && (
                            <button
                              type="button"
                              onClick={handleClearIllustration}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-destructive hover:bg-destructive/10"
                            >
                              <X className="w-3 h-3" />
                              Remove
                            </button>
                          )}
                        </div>
                        {illustrationError && (
                          <p className="text-[11px] text-destructive mt-1.5">{illustrationError}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <textarea
                  value={activeJsonText}
                  onChange={(e) => setActiveJsonText(e.target.value)}
                  placeholder={editorTab === "brief"
                    ? '{"version": "1.0", "region_key": "nordics_no", "steps": [...]}'
                    : '{"version": "1.0", "productType": "...", "sections": [...]}'
                  }
                  spellCheck={false}
                  className="w-full flex-1 resize-none rounded-lg border border-border bg-muted/50 p-4 font-mono text-xs leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />
          </>
          )}

          <ResizablePanel defaultSize={jsonCollapsed ? 100 : 55} minSize={30}>
            <ResizablePanelGroup direction="vertical">
              <ResizablePanel defaultSize={60} minSize={20}>
                <div className="flex flex-col h-full bg-background relative">
                  {(parsedConfig || parsedProduct || parsedPhotographer) && (
                    <button
                      type="button"
                      onClick={handleReset}
                      className="absolute top-1.5 right-2 z-10 text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                    >
                      Clear
                    </button>
                  )}

                  {parsedConfig ? (
                    <div className="flex-1 min-h-0">
                      <WizardPreview config={parsedConfig} productConfig={parsedProduct} />
                    </div>
                  ) : parsedProduct ? (
                    <div className="flex-1 min-h-0">
                      <ProductFormPreview config={parsedProduct} />
                    </div>
                  ) : parsedPhotographer ? (
                    <div className="flex-1 min-h-0 p-4 overflow-auto">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{parsedPhotographer.emoji || "📷"}</span>
                          <div>
                            <h3 className="font-semibold text-foreground">{parsedPhotographer.name || "Untitled"}</h3>
                            <p className="text-xs text-muted-foreground">{parsedPhotographer.description || ""}</p>
                          </div>
                        </div>
                        {parsedPhotographer.settings && (
                          <div className="rounded-lg border border-border bg-card p-3 space-y-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Settings</p>
                            {Object.entries(parsedPhotographer.settings).map(([key, value]) => (
                              <div key={key} className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">{key.replace(/_/g, " ")}</span>
                                <span className="font-medium text-foreground">{String(value)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center text-muted-foreground">
                        <p className="text-sm">No preview yet</p>
                        <p className="text-xs mt-1">Click Preview to see your config</p>
                      </div>
                    </div>
                  )}
                </div>
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={40} minSize={20}>
                <WizardAssistant
                  configType={editorTab}
                  currentJson={activeJsonText}
                  onApplyJson={applyAssistantJson}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Config dialog: linked configs + workspaces */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Config</DialogTitle>
            <DialogDescription className="sr-only">Linked configs and assigned workspaces.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {activeId && <ConfigLinker briefConfigId={activeId} />}
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Workspaces</p>
              <div className="space-y-0.5">
                {allWorkspaces.map((ws) => (
                  <label key={ws.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted rounded-md px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={assignedWorkspaceIds.has(ws.id)}
                      onChange={() => handleToggleWorkspace(ws.id)}
                      disabled={togglingWorkspaceId === ws.id}
                      className="rounded border-border"
                    />
                    {ws.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename {tabLabel}</DialogTitle>
            <DialogDescription>Give your {tabLabel.toLowerCase()} configuration a new name.</DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={`${tabLabel} name`}
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button onClick={handleRename} disabled={!renameValue.trim()}>Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {tabLabel}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{activeName}"? This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteUsage && (deleteUsage.briefs > 0 || deleteUsage.visuals > 0) && (
            <p className="text-xs text-muted-foreground">
              {[
                deleteUsage.briefs > 0 && `${deleteUsage.briefs} brief${deleteUsage.briefs === 1 ? "" : "s"}`,
                deleteUsage.visuals > 0 && `${deleteUsage.visuals} visual${deleteUsage.visuals === 1 ? "" : "s"}`,
              ].filter(Boolean).join(" and ")}{" "}
              reference this config. They are kept — the reference is cleared, not the row.
            </p>
          )}

          {deleteError && (
            <p className="text-xs text-destructive">{deleteError}</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
